import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import useTicket from "hooks/use-ticket";
import { useDetectOutsideClick } from "hooks/use-detect-outside-click";
import AngleDownSVG from "assets/svg/AngleDownSVG";
import { getTicketData, getTicketSupportAgents } from "redux/slices/ticket";
import { getSupportAgents } from "redux/slices/support-agents";
import {
  findSupportAgent,
  sortSupportAgentsByName,
  supportAgentLabel,
  supportAgentName,
} from "helpers/support-agent-name";

const AgentsDropdown = () => {
  const containerRef = useRef(null);

  const ticket = useSelector(getTicketData);
  const supportAgents = useSelector(getSupportAgents);
  const ticketSupportAgents = useSelector(getTicketSupportAgents);
  const sortedSupportAgents = useMemo(
    () => sortSupportAgentsByName(supportAgents),
    [supportAgents]
  );
  const { setTicketAgent } = useTicket();

  const [isActive, setIsActive] = useDetectOutsideClick(containerRef, false);

  const ticketId = ticket?.id;
  const responsibleId = ticket?.relationships?.responsible?.data?.id;
  const hasResponsible = responsibleId !== null && responsibleId !== undefined;

  // The agent list only holds admins flagged as support agents, so a ticket
  // can be assigned to someone outside it. Their name then comes from the
  // responsible included with the ticket.
  const listedAgent = findSupportAgent(supportAgents, responsibleId);
  const responsibleLabel = hasResponsible
    ? supportAgentLabel(
        listedAgent || findSupportAgent(ticketSupportAgents, responsibleId),
        responsibleId
      )
    : "";

  // An unlisted responsible stays in the menu while the ticket is open, so
  // reassigning away from them can be undone.
  const [unlistedAgent, setUnlistedAgent] = useState(null);
  useEffect(() => {
    if (hasResponsible && !listedAgent) {
      setUnlistedAgent({
        ticketId,
        id: responsibleId,
        label: responsibleLabel,
      });
    }
  }, [ticketId, responsibleId, hasResponsible, listedAgent, responsibleLabel]);

  const menuAgents = useMemo(() => {
    const listed = sortedSupportAgents.map((agent) => ({
      id: agent.id,
      label: supportAgentName(agent),
    }));
    return unlistedAgent?.ticketId === ticketId &&
      !findSupportAgent(supportAgents, unlistedAgent.id)
      ? [unlistedAgent, ...listed]
      : listed;
  }, [sortedSupportAgents, supportAgents, unlistedAgent, ticketId]);

  const handleToggleDropdown = useCallback(
    () => setIsActive(!isActive),
    [isActive, setIsActive]
  );

  const setAgentAndCloseDropdown = useCallback(
    (agentId) => {
      setIsActive(false);
      setTicketAgent(agentId);
    },
    [setIsActive, setTicketAgent]
  );

  return (
    <div className="menu-container" ref={containerRef}>
      <button
        onClick={handleToggleDropdown}
        className="menu-trigger"
        style={{ width: "270px" }}
      >
        <span>{responsibleLabel}</span>
        <AngleDownSVG className="dropDownAngleDownSVG" />
      </button>
      <nav className={`menu scrollable ${isActive ? "active" : "inactive"}`}>
        <ul>
          {menuAgents.map((agent) => (
            <li key={agent.id}>
              <button onClick={() => setAgentAndCloseDropdown(agent.id)}>
                {agent.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default memo(AgentsDropdown);
